import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ErrorTypeInput, SeverityLevelInput, UsageConflictEntry } from '@testflow/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mapApiError, usageConflictEntries } from '../../../lib/errors.js';
import { projectConfigApi } from '../../../lib/project-config-api.js';
import { toast } from '../../../lib/toast.js';
import { useCurrentProject } from '../../../lib/use-project.js';

// Plan 7 / Task F3: DEF-3-Vokabular (Severity-Stufen + Fehlertypen) editierbar je Projekt. Beide
// Tabellen sind unabhängig (eigener Speichern-Button, eigener PUT-Endpoint) — Änderungen an der
// einen Liste erzwingen kein Speichern der anderen. `_localId` ist reines UI-Konzept fürs
// React-`key`/Zeilen-Handling.
//
// Task B2 (#29 Finding 23): der Server macht seit Längerem KEIN delete-all+insert mehr, sondern
// Upsert-by-key (siehe CRITICAL-Kommentar in project-config.ts) — ein zurückgehaltener Key behält
// seine severity_id/error_type_id. Ein entfernter Key, der noch an Defects hängt, wird nicht mehr
// still auf null gesetzt: der PUT antwortet 409 severity_in_use/error_type_in_use und schreibt
// nichts. Deshalb behält der Zeilen-State jetzt die Server-Id UND usage_count — ohne die Zahl wüsste
// die UI nicht, ob ein ✕ harmlos ist oder erst umgehängt werden muss (POST .../reassign).
const COLOR_TOKENS = ['blocker', 'high', 'medium', 'low'] as const;

type SeverityRow = SeverityLevelInput & {
  _localId: string;
  severity_id: string | null;
  usage_count: number;
};
type ErrorTypeRow = ErrorTypeInput & {
  _localId: string;
  error_type_id: string | null;
  usage_count: number;
};

let localIdSeq = 0;
function nextLocalId(prefix: string): string {
  localIdSeq += 1;
  return `${prefix}-new-${localIdSeq}`;
}

// DEF-3: genau eine Severity-Stufe darf `is_default` sein (Auto-Defect-Vorschlag braucht einen
// eindeutigen Default). Wird clientseitig nach jeder Änderung erzwungen — der Server validiert das
// zusätzlich (422 `must_have_one_default`), das hier ist die HITL-freundliche Vorab-Absicherung.
function ensureOneDefault(rows: SeverityRow[]): SeverityRow[] {
  if (rows.length === 0) return rows;
  if (rows.filter((r) => r.is_default).length === 1) return rows;
  return rows.map((r, i) => ({ ...r, is_default: i === 0 }));
}

function withSortOrder<T extends { sort_order: number }>(rows: T[]): T[] {
  return rows.map((r, i) => ({ ...r, sort_order: i }));
}

function toSeverityPayload(row: SeverityRow): SeverityLevelInput {
  const { _localId, severity_id, usage_count, ...rest } = row;
  return rest;
}

function toErrorTypePayload(row: ErrorTypeRow): ErrorTypeInput {
  const { _localId, error_type_id, usage_count, ...rest } = row;
  return rest;
}

const inputClass =
  'w-full rounded-btn border border-[var(--border)] bg-bg-surface px-1.5 py-1 text-sm text-text-body';

// Task B2 (#29 Finding 23): Muster wörtlich aus DataKindRow (data-kinds-panel.tsx) — Ziel aus den
// ANDEREN, bereits gespeicherten Zeilen wählen, dann umhängen+löschen in einem Schritt. Nur Zeilen
// mit einer Server-Id sind gültige Ziele (eine gerade erst hinzugefügte, noch ungespeicherte Zeile
// existiert serverseitig nicht).
function ReassignControl({
  targetLabel,
  candidates,
  onReassign,
  pending,
}: {
  targetLabel: string;
  candidates: { id: string; label: string }[];
  onReassign: (targetId: string) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState('');
  return (
    <div className="flex items-center justify-end gap-1">
      <select
        aria-label={targetLabel}
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        className="rounded-btn border border-[var(--border)] bg-bg-surface px-1 py-1 text-xs text-text-body"
      >
        <option value="">{targetLabel}</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => target && onReassign(target)}
        disabled={!target || pending}
        className="shrink-0 rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {t('admin.vocab.reassign')}
      </button>
    </div>
  );
}

// Task B2 (#29 Finding 23): der 409-Konflikt aus dem Speichern zeigt die betroffenen Einträge
// namentlich — ein generischer Fehlertext allein würde nicht sagen, WELCHE Zeilen noch verwendet
// werden.
//
// Review-Fix B5: dieselbe Fläche trägt jetzt AUCH den generischen mapApiError-Text (z. B.
// must_have_one_default) und die clientseitigen Hinweise. Damit ist der Fehler dieser Mutationen
// vollständig inline sichtbar und der globale Toast wird per `meta.errorToast: false` abgeschaltet
// — die Repo-Regel aus query-client.ts, die genau diese Doppelmeldung verhindert.
function PanelAlert({ entries, messages }: { entries: UsageConflictEntry[]; messages: string[] }) {
  const { t } = useTranslation();
  if (entries.length === 0 && messages.length === 0) return null;
  return (
    <div
      role="alert"
      className="rounded-btn border border-status-fail/40 bg-status-fail/10 px-2 py-1.5 text-xs text-status-fail"
    >
      {messages.map((m) => (
        <p key={m}>{m}</p>
      ))}
      {entries.length > 0 && (
        <>
          <p>{t('admin.vocab.saveConflict')}</p>
          <ul className="list-disc pl-4">
            {entries.map((e) => (
              <li key={e.key}>
                {e.label_de} ({e.count})
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function VocabPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;

  const severityQuery = useQuery({
    queryKey: ['config-severity-levels', projectId],
    queryFn: () => projectConfigApi.getSeverityLevels(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  const errorTypeQuery = useQuery({
    queryKey: ['config-error-types', projectId],
    queryFn: () => projectConfigApi.getErrorTypes(projectId ?? ''),
    enabled: Boolean(projectId),
  });

  const [severityRows, setSeverityRows] = useState<SeverityRow[]>([]);
  const [errorTypeRows, setErrorTypeRows] = useState<ErrorTypeRow[]>([]);

  // Review-Fix B4: der Lade-Effekt überschreibt den kompletten Zeilen-State. Beim Speichern war das
  // unschädlich (lokaler Stand = Server-Stand), der Reassign ist aber der erste
  // Invalidierungs-Auslöser MITTEN in der Bearbeitung. Die Baseline hält den zuletzt geladenen
  // Server-Stand, damit ungespeicherte Änderungen erkennbar sind, bevor sie verloren gehen.
  const severityBaseline = useRef('');
  const errorTypeBaseline = useRef('');

  useEffect(() => {
    if (!severityQuery.data) return;
    const rows: SeverityRow[] = severityQuery.data.map((s) => ({
      key: s.key,
      label_de: s.label_de,
      label_en: s.label_en,
      rank: s.rank,
      color_token: s.color_token as SeverityLevelInput['color_token'],
      is_default: s.is_default,
      sort_order: s.sort_order,
      _localId: s.severity_id,
      severity_id: s.severity_id,
      usage_count: s.usage_count,
    }));
    severityBaseline.current = JSON.stringify(withSortOrder(rows));
    setSeverityRows(rows);
  }, [severityQuery.data]);

  useEffect(() => {
    if (!errorTypeQuery.data) return;
    const rows: ErrorTypeRow[] = errorTypeQuery.data.map((e) => ({
      key: e.key,
      label_de: e.label_de,
      label_en: e.label_en,
      sort_order: e.sort_order,
      _localId: e.error_type_id,
      error_type_id: e.error_type_id,
      usage_count: e.usage_count,
    }));
    errorTypeBaseline.current = JSON.stringify(withSortOrder(rows));
    setErrorTypeRows(rows);
  }, [errorTypeQuery.data]);

  // sort_order wird beim Speichern ohnehin neu vergeben — beide Seiten normalisiert vergleichen,
  // damit eine Lücke in der Server-Reihenfolge nicht als „ungespeicherte Änderung" gilt.
  const severityDirty = JSON.stringify(withSortOrder(severityRows)) !== severityBaseline.current;
  const errorTypeDirty = JSON.stringify(withSortOrder(errorTypeRows)) !== errorTypeBaseline.current;

  // Task B2 (#29 Finding 23): der 409 aus dem Speichern nennt die noch verwendeten Einträge.
  // Review-Fix B5: `messages` trägt zusätzlich den generischen Fehlertext (alles, was NICHT
  // severity_in_use/error_type_in_use ist, z. B. must_have_one_default) sowie clientseitige
  // Hinweise — deshalb darf der globale Toast dieser Mutationen aus (kein doppelter Fehler).
  const [severityConflict, setSeverityConflict] = useState<UsageConflictEntry[]>([]);
  const [errorTypeConflict, setErrorTypeConflict] = useState<UsageConflictEntry[]>([]);
  const [severityMessages, setSeverityMessages] = useState<string[]>([]);
  const [errorTypeMessages, setErrorTypeMessages] = useState<string[]>([]);

  const saveSeverities = useMutation({
    mutationFn: (items: SeverityLevelInput[]) =>
      projectConfigApi.putSeverityLevels(projectId ?? '', items),
    meta: { successToast: 'feedback.saved', errorToast: false },
    onMutate: () => {
      setSeverityConflict([]);
      setSeverityMessages([]);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config-severity-levels', projectId] });
      // Auch den Defect-Board/-Detail-Cache invalidieren, der dasselbe Vokabular über
      // /defect-config liest — sonst erscheint eine neue/geänderte Severity dort erst nach Reload.
      void queryClient.invalidateQueries({ queryKey: ['defect-config', projectId] });
    },
    onError: (err) => {
      const entries = usageConflictEntries(err);
      setSeverityConflict(entries ?? []);
      setSeverityMessages(entries ? [] : [mapApiError(t, err)]);
    },
  });
  const saveErrorTypes = useMutation({
    mutationFn: (items: ErrorTypeInput[]) => projectConfigApi.putErrorTypes(projectId ?? '', items),
    meta: { successToast: 'feedback.saved', errorToast: false },
    onMutate: () => {
      setErrorTypeConflict([]);
      setErrorTypeMessages([]);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config-error-types', projectId] });
      // Siehe oben: Defect-Dropdowns lesen /defect-config → sofort mit-invalidieren.
      void queryClient.invalidateQueries({ queryKey: ['defect-config', projectId] });
    },
    onError: (err) => {
      const entries = usageConflictEntries(err);
      setErrorTypeConflict(entries ?? []);
      setErrorTypeMessages(entries ? [] : [mapApiError(t, err)]);
    },
  });

  // Task B2 (#29 Finding 23): Umhängen ist ein eigener, sofortiger Server-Aufruf (wie
  // dataKindsApi.merge) — kein Teil des Speichern-Batches. invalidateQueries lädt die betroffene
  // Liste danach neu, statt die entfernte Zeile nur lokal auszublenden.
  // Review-Fix B9: die Erfolgsmeldung nennt die Zahl der umgehängten Defects (`moved`) — deshalb
  // kein statischer `meta.successToast` (der kann keine Interpolation), sondern ein eigener Toast.
  const reassignSeverity = useMutation({
    mutationFn: ({ severityId, targetId }: { severityId: string; targetId: string }) =>
      projectConfigApi.reassignSeverity(projectId ?? '', severityId, targetId),
    onSuccess: (data) => {
      toast.success(t('admin.vocab.reassigned', { count: data.moved }));
      void queryClient.invalidateQueries({ queryKey: ['config-severity-levels', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['defect-config', projectId] });
    },
  });
  const reassignErrorType = useMutation({
    mutationFn: ({ errorTypeId, targetId }: { errorTypeId: string; targetId: string }) =>
      projectConfigApi.reassignErrorType(projectId ?? '', errorTypeId, targetId),
    onSuccess: (data) => {
      toast.success(t('admin.vocab.reassigned', { count: data.moved }));
      void queryClient.invalidateQueries({ queryKey: ['config-error-types', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['defect-config', projectId] });
    },
  });

  // Review-Fix B4: der Reassign invalidiert die Liste, der Lade-Effekt setzt danach den kompletten
  // Zeilen-State neu — ungespeicherte Änderungen wären ohne Hinweis weg. Deshalb erst speichern
  // lassen, statt sie still zu verwerfen.
  function requestSeverityReassign(severityId: string, targetId: string) {
    if (severityDirty) {
      setSeverityMessages([t('admin.vocab.reassignUnsaved')]);
      return;
    }
    setSeverityMessages([]);
    reassignSeverity.mutate({ severityId, targetId });
  }
  function requestErrorTypeReassign(errorTypeId: string, targetId: string) {
    if (errorTypeDirty) {
      setErrorTypeMessages([t('admin.vocab.reassignUnsaved')]);
      return;
    }
    setErrorTypeMessages([]);
    reassignErrorType.mutate({ errorTypeId, targetId });
  }

  function updateSeverityRow(localId: string, patch: Partial<SeverityRow>) {
    setSeverityRows((rows) =>
      ensureOneDefault(rows.map((r) => (r._localId === localId ? { ...r, ...patch } : r))),
    );
  }

  function setDefaultSeverity(localId: string) {
    setSeverityRows((rows) => rows.map((r) => ({ ...r, is_default: r._localId === localId })));
  }

  function addSeverityRow() {
    setSeverityRows((rows) =>
      ensureOneDefault([
        ...rows,
        {
          _localId: nextLocalId('severity'),
          severity_id: null,
          usage_count: 0,
          key: '',
          label_de: '',
          label_en: '',
          rank: rows.length,
          color_token: 'low',
          is_default: false,
          sort_order: rows.length,
        },
      ]),
    );
  }

  // Task B2 (#29 Finding 23): kein stilles Löschen mehr, wenn eine Zeile noch verwendet wird — der
  // ✕-Handler entfernt nur Zeilen mit usage_count 0 (das rendert die Tabelle unten entsprechend gar
  // nicht erst als ✕, sondern als Zielauswahl; dieser Guard ist die zweite Absicherung).
  function removeSeverityRow(localId: string) {
    setSeverityRows((rows) =>
      ensureOneDefault(rows.filter((r) => r._localId !== localId || r.usage_count > 0)),
    );
  }

  function updateErrorTypeRow(localId: string, patch: Partial<ErrorTypeRow>) {
    setErrorTypeRows((rows) => rows.map((r) => (r._localId === localId ? { ...r, ...patch } : r)));
  }

  function addErrorTypeRow() {
    setErrorTypeRows((rows) => [
      ...rows,
      {
        _localId: nextLocalId('error-type'),
        error_type_id: null,
        usage_count: 0,
        key: '',
        label_de: '',
        label_en: '',
        sort_order: rows.length,
      },
    ]);
  }

  function removeErrorTypeRow(localId: string) {
    setErrorTypeRows((rows) => rows.filter((r) => r._localId !== localId || r.usage_count > 0));
  }

  if (projectLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }
  if (!project || !projectId) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <h2 className="text-lg font-bold text-text-primary">{t('admin.vocab.navLabel')}</h2>

      <section className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-text-primary">{t('admin.vocab.severityTitle')}</h3>
          <button
            type="button"
            onClick={addSeverityRow}
            className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10"
          >
            + {t('admin.vocab.addSeverity')}
          </button>
        </div>

        {severityQuery.isLoading && (
          <div className="text-sm text-text-muted">{t('common.loading')}</div>
        )}
        {!severityQuery.isLoading && severityRows.length === 0 && (
          <div className="text-sm text-text-muted">{t('admin.vocab.empty')}</div>
        )}
        {severityRows.length > 0 && (
          <div className="overflow-x-auto rounded-card border border-[var(--border)]">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wide text-text-muted">
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.key')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.labelDe')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.labelEn')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.rank')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.color')}</th>
                  <th className="px-2 py-2 text-center">{t('admin.vocab.columns.default')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.usage')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {severityRows.map((row) => (
                  <tr key={row._localId} className="border-b border-[var(--border)] last:border-0">
                    <td className="px-2 py-1.5">
                      {/* Review-Fix B7: der Schlüssel IST die Identität der Zeile (Upsert-by-key).
                          Ihn an einem verwendeten Eintrag zu ändern hiess bisher: alter Key fehlt im
                          Payload → 409 „bitte erst umhängen" → Umhängen löscht den Eintrag, also
                          eine Sackgasse. Bezeichnungen bleiben änderbar. */}
                      <input
                        value={row.key}
                        onChange={(e) => updateSeverityRow(row._localId, { key: e.target.value })}
                        readOnly={row.usage_count > 0 && Boolean(row.severity_id)}
                        title={
                          row.usage_count > 0 && row.severity_id
                            ? t('admin.vocab.keyLocked')
                            : undefined
                        }
                        className={inputClass}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={row.label_de}
                        onChange={(e) =>
                          updateSeverityRow(row._localId, { label_de: e.target.value })
                        }
                        className={inputClass}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={row.label_en}
                        onChange={(e) =>
                          updateSeverityRow(row._localId, { label_en: e.target.value })
                        }
                        className={inputClass}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        value={row.rank}
                        onChange={(e) =>
                          updateSeverityRow(row._localId, { rank: Number(e.target.value) })
                        }
                        className={`${inputClass} w-16`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        value={row.color_token}
                        onChange={(e) =>
                          updateSeverityRow(row._localId, {
                            color_token: e.target.value as SeverityLevelInput['color_token'],
                          })
                        }
                        className={inputClass}
                      >
                        {COLOR_TOKENS.map((c) => (
                          <option key={c} value={c}>
                            {t(`admin.vocab.colors.${c}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="radio"
                        name="severity-default"
                        checked={row.is_default}
                        onChange={() => setDefaultSeverity(row._localId)}
                        aria-label={t('admin.vocab.setDefault')}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-text-muted">
                      {t('admin.vocab.usageCount', { count: row.usage_count })}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {/* Review-Fix B1: die Standard-Stufe lässt sich nicht umhängen — das Umhängen
                          löscht die Quelle, das Projekt bliebe ohne Default zurück (Server: 422
                          reassign_default_severity). Der Ausweg steht in derselben Tabelle: eine
                          andere Stufe als Standard markieren und speichern. */}
                      {row.usage_count > 0 && row.severity_id && row.is_default ? (
                        <span className="text-xs text-text-muted">
                          {t('admin.vocab.reassignDefaultBlocked')}
                        </span>
                      ) : row.usage_count > 0 && row.severity_id ? (
                        <ReassignControl
                          targetLabel={t('admin.vocab.reassignTarget')}
                          candidates={severityRows
                            .filter((r) => r.severity_id && r.severity_id !== row.severity_id)
                            .map((r) => ({ id: r.severity_id!, label: r.label_de || r.key }))}
                          pending={reassignSeverity.isPending}
                          onReassign={(targetId) =>
                            requestSeverityReassign(row.severity_id!, targetId)
                          }
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeSeverityRow(row._localId)}
                          aria-label={t('admin.vocab.removeSeverity')}
                          title={t('admin.vocab.removeSeverity')}
                          className="rounded-btn px-1 text-text-muted hover:text-status-fail"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PanelAlert entries={severityConflict} messages={severityMessages} />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() =>
              saveSeverities.mutate(withSortOrder(severityRows).map(toSeverityPayload))
            }
            disabled={saveSeverities.isPending}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('admin.vocab.saveSeverities')}
          </button>
        </div>
      </section>

      <section className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-text-primary">{t('admin.vocab.errorTypeTitle')}</h3>
          <button
            type="button"
            onClick={addErrorTypeRow}
            className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10"
          >
            + {t('admin.vocab.addErrorType')}
          </button>
        </div>

        {errorTypeQuery.isLoading && (
          <div className="text-sm text-text-muted">{t('common.loading')}</div>
        )}
        {!errorTypeQuery.isLoading && errorTypeRows.length === 0 && (
          <div className="text-sm text-text-muted">{t('admin.vocab.empty')}</div>
        )}
        {errorTypeRows.length > 0 && (
          <div className="overflow-x-auto rounded-card border border-[var(--border)]">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wide text-text-muted">
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.key')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.labelDe')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.labelEn')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.sortOrder')}</th>
                  <th className="px-2 py-2 text-left">{t('admin.vocab.columns.usage')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {errorTypeRows.map((row) => (
                  <tr key={row._localId} className="border-b border-[var(--border)] last:border-0">
                    <td className="px-2 py-1.5">
                      {/* Review-Fix B7, siehe Severity-Tabelle oben. */}
                      <input
                        value={row.key}
                        onChange={(e) => updateErrorTypeRow(row._localId, { key: e.target.value })}
                        readOnly={row.usage_count > 0 && Boolean(row.error_type_id)}
                        title={
                          row.usage_count > 0 && row.error_type_id
                            ? t('admin.vocab.keyLocked')
                            : undefined
                        }
                        className={inputClass}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={row.label_de}
                        onChange={(e) =>
                          updateErrorTypeRow(row._localId, { label_de: e.target.value })
                        }
                        className={inputClass}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={row.label_en}
                        onChange={(e) =>
                          updateErrorTypeRow(row._localId, { label_en: e.target.value })
                        }
                        className={inputClass}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        value={row.sort_order}
                        onChange={(e) =>
                          updateErrorTypeRow(row._localId, { sort_order: Number(e.target.value) })
                        }
                        className={`${inputClass} w-16`}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-text-muted">
                      {t('admin.vocab.usageCount', { count: row.usage_count })}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {row.usage_count > 0 && row.error_type_id ? (
                        <ReassignControl
                          targetLabel={t('admin.vocab.reassignTarget')}
                          candidates={errorTypeRows
                            .filter((r) => r.error_type_id && r.error_type_id !== row.error_type_id)
                            .map((r) => ({ id: r.error_type_id!, label: r.label_de || r.key }))}
                          pending={reassignErrorType.isPending}
                          onReassign={(targetId) =>
                            requestErrorTypeReassign(row.error_type_id!, targetId)
                          }
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeErrorTypeRow(row._localId)}
                          aria-label={t('admin.vocab.removeErrorType')}
                          title={t('admin.vocab.removeErrorType')}
                          className="rounded-btn px-1 text-text-muted hover:text-status-fail"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PanelAlert entries={errorTypeConflict} messages={errorTypeMessages} />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() =>
              saveErrorTypes.mutate(withSortOrder(errorTypeRows).map(toErrorTypePayload))
            }
            disabled={saveErrorTypes.isPending}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('admin.vocab.saveErrorTypes')}
          </button>
        </div>
      </section>
    </div>
  );
}
